/**
 * Genre Spotlight Block
 * Browse by genre with category tabs and grid
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Grid } from 'lucide-react';
import { SectionHeader, SeriesCardVertical, ScrollContainer } from './shared';
import { API_URL } from '../../config/api';

const API = API_URL;

const GenreSpotlightBlock = ({ data = { categories: [], default_series: [] }, settings = {} }) => {
  const scrollRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categorySeries, setCategorySeries] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const {
    title = 'Browse by Genre',
    max_items = 12,
    card_scale = 0.85,
    max_image_height = 220,
    columns = { desktop: 5, tablet: 3, mobile: 2 },
    featured_genres = [],
  } = settings;

  const categories = data.categories || [];
  const defaultSeries = data.default_series || [];

  // Fetch series when category changes
  useEffect(() => {
    if (selectedCategory && selectedCategory !== 'All') {
      fetchCategorySeries(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchCategorySeries = async (category) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/homepage/by-category/${encodeURIComponent(category)}`);
      setCategorySeries(response.data || []);
    } catch (error) {
      console.error('Error fetching category series:', error);
      setCategorySeries([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter categories if featured_genres is set
  const displayCategories = featured_genres.length > 0
    ? ['All', ...categories.filter(c => featured_genres.includes(c))]
    : ['All', ...categories];

  const displaySeries = selectedCategory === 'All' 
    ? defaultSeries.slice(0, max_items)
    : categorySeries.slice(0, max_items);

  if (categories.length === 0 && defaultSeries.length === 0) return null;

  return (
    <section data-testid="genre-spotlight-block">
      <SectionHeader 
        title={title}
        icon={Grid}
        iconColor="text-purple-400"
      />

      {/* Category chips */}
      <div className="relative mb-4 sm:mb-6">
        <div className="max-w-screen-2xl mx-auto px-2 sm:px-6">
          <div
            ref={scrollRef}
            className="flex items-center space-x-1.5 sm:space-x-2 overflow-x-auto scrollbar-hide py-2 sm:py-3 scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {displayCategories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`flex-shrink-0 px-3 py-1.5 sm:px-5 sm:py-2 rounded-full text-xs sm:text-sm font-bold transition-all ${
                  selectedCategory === category
                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/50'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Series grid */}
      <div 
        className={`grid gap-3 sm:gap-4 px-4 sm:px-6 ${loading ? 'opacity-50' : ''}`}
        style={{
          gridTemplateColumns: `repeat(${columns.mobile}, 1fr)`,
        }}
      >
        <style>{`
          @media (min-width: 640px) {
            [data-testid="genre-spotlight-block"] > div:last-child {
              grid-template-columns: repeat(${columns.tablet}, 1fr) !important;
            }
          }
          @media (min-width: 1024px) {
            [data-testid="genre-spotlight-block"] > div:last-child {
              grid-template-columns: repeat(${columns.desktop}, 1fr) !important;
            }
          }
        `}</style>
        {displaySeries.map((series) => (
          <SeriesCardVertical 
            key={series.id} 
            series={series}
            settings={{ card_scale, max_image_height }}
          />
        ))}
      </div>
    </section>
  );
};

export default GenreSpotlightBlock;
